import {Palette, shuffleArray} from 'hubbl-shared';
import {memo, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {AssetIconAI} from '../../assets';
import {useI18n} from '../../hooks';

interface AiSuggestionButtonProps {
  suggestions: Array<{
    description: {en: string; da: string};
    emoji: string;
  }>;
  onSuggestionSelect: (suggestion: {
    description: string;
    emoji: string;
  }) => void;
  preferredLanguage?: string;
}

const Button = styled.button`
  width: 48px;
  height: 48px;
  border: 2px solid #e0e0e0;
  background: ${Palette.primarySubtleGreen};
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${Palette.primaryTerraCotta};
    background: ${Palette.primaryTerraCotta};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 70%
    );
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover::before {
    transform: translateX(100%);
  }
`;

export const AiSuggestionButton = memo(
  ({
    suggestions,
    onSuggestionSelect,
    preferredLanguage = 'en',
  }: AiSuggestionButtonProps) => {
    const t = useI18n();
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const randomizedSuggestions = useMemo(() => {
      if (!suggestions.length) return [];
      const newArray = [...suggestions];
      shuffleArray(newArray);
      return newArray;
    }, [suggestions]);

    const handleSuggestion = useCallback(() => {
      if (!randomizedSuggestions.length) return;

      const newIndex = (currentIndex + 1) % randomizedSuggestions.length;
      const suggestion = randomizedSuggestions[newIndex];

      onSuggestionSelect({
        description: suggestion.description[preferredLanguage as 'en' | 'da'],
        emoji: suggestion.emoji,
      });
      setCurrentIndex(newIndex);
    }, [
      currentIndex,
      randomizedSuggestions,
      onSuggestionSelect,
      preferredLanguage,
    ]);

    if (!suggestions.length) return null;

    return (
      <Button
        type="button"
        onClick={handleSuggestion}
        title={t.ai.getAISuggestion}>
        <AssetIconAI />
      </Button>
    );
  },
);
